import dayjs from 'dayjs';
export function mapBookingFromDto(dto) {
    var _a;
    return {
        bookingId: dto.id,
        bookingStartsAt: dto.expectedArrivalAt ? new Date(dto.expectedArrivalAt) : undefined,
        bookingEndsAt: dto.expectedDepartureAt ? new Date(dto.expectedDepartureAt) : undefined,
        photographerName: (_a = dto.photographer) === null || _a === void 0 ? void 0 : _a.name,
        status: dto.bookingStatus,
        address: dto.address,
        parentBookingId: dto.parentBookingId,
    };
}
/**
 * Efkt has no `CONFIRMED`. The page asks one binary question — has a slot been picked? — so every
 * status at or past `Booked` has to answer yes, or a seller who is already booked gets invited to
 * book again. Same set as `v2/modules/tasks/hooks.tsx`'s `SCHEDULED_BOOKING_STATUSES`, `Cancelled`
 * deliberately excluded from it there and here.
 *
 * `Completed` lands on `CONFIRMED` rather than datahub's `COMPLETE` on purpose: on this page
 * `COMPLETE` falls through to the picker, and a shoot that already happened must not offer one. That
 * is why this is not `EFKT_TO_DATAHUB_BOOKING_STATUS` (`v2/modules/orders/hooks.tsx`) — that map
 * feeds `IBookingInfo`, where `Completed` genuinely means `COMPLETE`. Same vocabulary, different
 * question; keep the two apart.
 *
 * `Cancelled` has no branch on the page and falls through to the picker exactly as `INCOMPLETE`
 * does — which is what the datahub arm does with a cancelled booking too, so it stays that way. The
 * entry is not therefore redundant: without it a cancelled shoot would be *reported* as
 * `INCOMPLETE`, and this model claims to speak datahub's vocabulary. In practice such a booking has
 * no proposals, so the page lands on its cannot-be-booked fallback rather than an empty picker.
 */
const EFKT_TO_PUBLIC_BOOKING_STATUS = {
    Booked: 'CONFIRMED',
    Locked: 'CONFIRMED',
    OnRoute: 'CONFIRMED',
    Arrived: 'CONFIRMED',
    Completed: 'CONFIRMED',
    Cancelled: 'CANCELLED',
};
/**
 * `GET /orders/{salesOrderId}/booking-info` -> the same model, in datahub's vocabulary, that the
 * datahub arm's own `mapPublicBookingFromDto` produces. `orderId` is the caller's path segment: the
 * response does not carry it.
 */
export function mapPublicBookingFromCompatDto(dto, orderId) {
    var _a;
    return {
        orderId,
        bookingId: dto.bookingId,
        status: (_a = EFKT_TO_PUBLIC_BOOKING_STATUS[dto.status]) !== null && _a !== void 0 ? _a : 'INCOMPLETE',
    };
}
/**
 * Ported verbatim from `datahub/tasks/helpers.ts::normalizeBookingPhotographerData`. Parses
 * `proposal.notes` — an HTML-ish string, lines joined by `<BR/>` — for an optional `Area: N m2`
 * value and per-line `{time, text}` info entries. Kept identical rather than simplified: this is
 * the exact contract booqly's UI already renders against, and the mocked Efkt shape is assumed to
 * carry the same `notes` convention until BE says otherwise.
 */
function normalizeBookingPhotographerData({ proposal, duration, bookingId, }) {
    const arrival = new Date(proposal.expectedStart), departure = dayjs(arrival).add(duration, 'm').toDate(), start = new Date(proposal.timeframeStart), end = new Date(proposal.timeframeEnd);
    let area, info;
    if (proposal.notes) {
        const infoStr = proposal.notes.trim();
        const timeRegExp = /\d{4}-\d{2}-\d{2} \d{2}:\d{2}/g;
        const lineBreakExp = /(?:<(?:br|BR) ?\/>)+/g;
        const areaExp = /(?:Area: )(.*)(?= m2)/g;
        // split string in array using <BR/> tag as separator
        const infoArr = infoStr.split(lineBreakExp).filter(str => {
            const parsedArea = str === null || str === void 0 ? void 0 : str.match(areaExp);
            // if element has Area info - get area and exclude this element from info array
            if (parsedArea) {
                area = parsedArea;
                return false;
            }
            else {
                return true;
            }
        });
        // if element has timestamp - cut it and return as separate property
        if (infoArr.length) {
            info = infoArr.map(str => {
                // format always the same
                if (~str.search(timeRegExp)) {
                    return {
                        time: new Date(str.trim().slice(0, 16)),
                        text: str.trim().slice(17),
                    };
                }
                else {
                    return { text: str };
                }
            });
        }
    }
    return {
        start,
        end,
        timeForTask: duration,
        info,
        area,
        arrival,
        departure,
        bookingId,
        id: proposal.proposalId,
        photographerName: proposal.photographerName ? proposal.photographerName : undefined,
    };
}
/**
 * Ported from `datahub/tasks/helpers.ts::mapCallProposalFromDto`. Caller decides whether to call
 * this at all — matching datahub, an absent `suggestedProposal` means the caller should treat the
 * whole read as "no proposal" (see `BookingsApi.getBookingProposal`'s own comment for why).
 */
export function mapCallProposalFromDto(proposalResponse) {
    var _a;
    return {
        primary: proposalResponse.suggestedProposal
            ? normalizeBookingPhotographerData({
                proposal: proposalResponse.suggestedProposal,
                duration: proposalResponse.durationMinutes,
                bookingId: proposalResponse.bookingId,
            })
            : undefined,
        secondary: ((_a = proposalResponse.proposalList) === null || _a === void 0 ? void 0 : _a.map(proposal => normalizeBookingPhotographerData({
            proposal,
            duration: proposalResponse.durationMinutes,
            bookingId: proposalResponse.bookingId,
        }))) || [],
        duration: proposalResponse.durationMinutes,
    };
}
